import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { ReactElement, useRef } from 'react';

interface Props {
  maxFileNumber?: number;
  onMaxFileNumberError?: (totalFilesReceived: number) => void;
  maxFilesSize?: number;
  onMaxFilesSizeError?: (totalFilesSizeReceived: number) => void;
  uploading: boolean;
  multiple?: boolean;
  uploadProgress?: string;
  onSelectFiles: (files: File[]) => void;
  uploadDone: boolean;
  selectFileMessage?: string | ReactElement;
  uploadedSuccessMessage?: string | ReactElement;
  acceptFiles?: string[];
}
// eslint-disable-next-line import/prefer-default-export
export function MultiFileUpload({
  maxFileNumber,
  maxFilesSize,
  multiple,
  uploading,
  uploadDone,
  uploadProgress,
  selectFileMessage = (
    <>
      Click to choose files
      <br />
      or drag them here.
    </>
  ),
  uploadedSuccessMessage = 'Files Uploaded Successfully',
  acceptFiles = [],
  onSelectFiles,
  onMaxFileNumberError,
  onMaxFilesSizeError,
}: Props) {
  const inputRef = useRef(null);

  // eslint-disable-next-line func-names
  const handleDrag = function (e: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    e.preventDefault();
    e.stopPropagation();
  };
  // eslint-disable-next-line func-names
  const handleDrop = function (e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleUploadFiles(e.dataTransfer.files);
    }
  };

  // eslint-disable-next-line consistent-return
  const handleUploadFiles = (fileInput: FileList) => {
    const files: File[] = [];
    let uploadFiles = true;

    if (onMaxFilesSizeError && maxFilesSize) {
      const totalFilesSize: number = Array.from(fileInput).reduce(
        (accum, current) => accum + current.size,
        0
      );

      if (totalFilesSize > maxFilesSize) {
        onMaxFilesSizeError(totalFilesSize);
        uploadFiles = false;
      }
    }

    if (
      onMaxFileNumberError &&
      maxFileNumber &&
      fileInput.length > maxFileNumber
    ) {
      onMaxFileNumberError(fileInput.length);
      uploadFiles = false;
    }

    if (!uploadFiles) {
      return true;
    }

    Array.from(fileInput).forEach((file) => {
      if (files.findIndex((f) => f.name === file.name) === -1) {
        files.push(file);
      }
    });

    onSelectFiles(files);
  };

  const handleFileEvent = (e: { target: HTMLInputElement }) => {
    const chosenFiles = e.target.files;
    if (chosenFiles?.length) {
      handleUploadFiles(chosenFiles);
    }
  };
  return (
    <form
      id='form-file-upload'
      onSubmit={(e) => e.preventDefault()}
      onDragEnter={handleDrag}
    >
      <input
        ref={inputRef}
        type='file'
        id='input-file-upload'
        multiple={multiple}
        accept={acceptFiles.join(', ')}
        onChange={handleFileEvent}
        className='hidden'
        disabled={uploading || uploadDone}
      />
      <div
        id='drag-file-element'
        data-testid='drag-file-element'
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <label id='label-file-upload' htmlFor='input-file-upload'>
          <div className='h-[120px]'>
            {!uploadDone && !uploading && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a role='button' className='btn  normal-case w-full h-[120px]'>
                <div>
                  <ArrowUpTrayIcon className='w-16 text-primary mx-auto' />
                </div>
                {selectFileMessage}
              </a>
            )}
            {uploading && (
              <div className='w-full h-[120px] flex flex-col content-center justify-center items-center'>
                <progress
                  className='progress progress-primary w-56 m-[8px]'
                  value={uploadProgress}
                  max='100'
                />
                Uploading files
              </div>
            )}
            {uploadDone && (
              <div className='w-full h-[120px] flex flex-col content-center justify-center items-center'>
                {uploadedSuccessMessage}
              </div>
            )}
          </div>
        </label>
      </div>
    </form>
  );
}
